<template>
	<div
		class="ios-select-proxy"
		:class="{
			'is-open': menuOpen,
			'has-clear': showClearBtn && (searchText || (clearAlsoValue && innerValue !== null && innerValue !== '' && innerValue !== undefined)),
			'has-arrow': showArrow,
			'has-hint': searchText && searchText.trim().length < 3,
		}"
	>
		<el-select
			ref="sel"
			v-model="innerValue"
			class="clase-el-select"
			:popper-append-to-body="true"
			:popper-class="'ios-popper'"
			placeholder=" "
			@change="onChange"
			@visible-change="onVisibleChange"
		>
			<el-option
				class="ios-option"
				v-for="(opt, idx) in filteredOptions"
				:key="`${field}-${String(opt[valueKey])}-${idx}`"
				:label="opt[labelKey]"
				:value="opt[valueKey]"
			/>
			<template slot="empty">
				<div class="no-data-hint">
					{{ loading ? 'Cargando…' : 'Sin datos' }}
				</div>
			</template>
		</el-select>
		<input
			ref="input"
			class="proxy-input"
			type="text"
			:placeholder="placeholder || 'Seleccionar...'"
			v-model="searchText"
			autocomplete="off"
			autocapitalize="none"
			autocorrect="off"
			:disabled="disabled"
			@click.stop="onInteract"
			@touchend.stop="onInteract"
			@input.stop="onInteract"
			@blur="onBlurInput"
		/>
		<button
			v-if="showClearBtn && (searchText || (clearAlsoValue && innerValue !== null && innerValue !== '' && innerValue !== undefined))"
			class="clear-btn"
			type="button"
			aria-label="Limpiar"
			@mousedown.prevent
			@click.stop="clearInput"
		>
			✕
		</button>
		<div v-if="menuOpen" class="ios-backdrop" @wheel.prevent @touchmove.prevent @click="closeMenu"></div>
		<i v-if="showArrow" class="el-icon-arrow-down arrow-icon"></i>
	</div>
</template>
<script>
const ACTIVE_KEY = '__iosSelectActive__';
// Buscar el contenedor scrolleable más cercano
function getScrollContainer(el) {
	let node = el;
	while (node && node !== document.body) {
		const style = window.getComputedStyle(node);
		if (/(auto|scroll)/.test(style.overflowY)) return node;
		node = node.parentElement;
	}
	return document.scrollingElement || document.documentElement || document.body;
}
// Lockeo de scroll local, solo sobre el contenedor scrolleable padre del host
function createLocalScrollLock() {
	let target = null;
	let savedScroll = 0;
	let prevOverflowY = '';
	let allowedEl = null;
	let wheelHandler = null;
	let touchHandler = null;
	let keyHandler = null;

	const isInsideAllowed = node => allowedEl && (node === allowedEl || allowedEl.contains(node));

	const onWheel = e => {
		if (!isInsideAllowed(e.target)) {
			e.preventDefault();
			e.stopPropagation();
			return false;
		}
		try {
			const atTop = allowedEl.scrollTop <= 0 && e.deltaY < 0;
			const atBottom = allowedEl.scrollTop + allowedEl.clientHeight >= allowedEl.scrollHeight && e.deltaY > 0;
			if (atTop || atBottom) {
				e.preventDefault();
				e.stopPropagation();
				return false;
			}
		} catch {}
	};

	const onTouchMove = e => {
		if (!isInsideAllowed(e.target)) {
			e.preventDefault();
			e.stopPropagation();
			return false;
		}
	};

	const onKeyDown = e => {
		const keys = ['PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown', ' '];
		if (keys.includes(e.key)) {
			if (!isInsideAllowed(document.activeElement)) {
				e.preventDefault();
				e.stopPropagation();
				return false;
			}
		}
	};

	return {
		lock(hostEl, dropdownWrapEl) {
			target = getScrollContainer(hostEl);
			allowedEl = dropdownWrapEl || null;

			// guardar estado y bloquear solo el contenedor
			savedScroll = target.scrollTop;
			prevOverflowY = target.style.overflowY;
			target.style.overflowY = 'hidden';

			// listeners SOLO sobre el contenedor (no global)
			wheelHandler = onWheel;
			touchHandler = onTouchMove;
			keyHandler = onKeyDown;

			target.addEventListener('wheel', wheelHandler, { capture: true, passive: false });
			target.addEventListener('touchmove', touchHandler, { capture: true, passive: false });
			target.addEventListener('keydown', keyHandler, { capture: true });
		},
		updateAllowed(el) {
			allowedEl = el || null;
		},
		unlock() {
			if (!target) return;
			target.removeEventListener('wheel', wheelHandler, { capture: true });
			target.removeEventListener('touchmove', touchHandler, { capture: true });
			target.removeEventListener('keydown', keyHandler, { capture: true });

			target.style.overflowY = prevOverflowY || '';
			target.scrollTop = savedScroll || 0;

			target = null;
			allowedEl = null;
			wheelHandler = touchHandler = keyHandler = null;
		},
	};
}
export default {
	name: 'IosSelectMini',
	props: {
		field: { type: String, required: false },
		value: [String, Number, null],
		options: { type: Array, default: () => [] },
		labelKey: { type: String, default: 'label' },
		valueKey: { type: String, default: 'value' },
		placeholder: String,
		disabled: Boolean,
		loading: Boolean,
		clearOnSelect: { type: Boolean, default: false },
		clearOnOutsideNoValue: { type: Boolean, default: true },
		showClearBtn: { type: Boolean, default: true },
		clearAlsoValue: { type: Boolean, default: true },
		showArrow: { type: Boolean, default: true },
	},
	data() {
		return {
			innerValue: this.value,
			searchText: '',
			openTimer: 0,
			menuOpen: false,
			pendingOpen: false,
			refocusTimer: 0,
			scrollListeners: [],
			scrollLock: null,
		};
	},
	computed: {
		// Opciones sin duplicados (por valueKey)
		baseOptions() {
			const rawOpts = Array.isArray(this.options) ? this.options : [];
			const seen = new Set();
			const out = [];
			for (const o of rawOpts) {
				const raw = o[this.valueKey];
				const key = raw == null ? '__null__' : String(raw);
				if (seen.has(key)) continue;
				seen.add(key);
				out.push(o);
			}
			return out;
		},
		// Opciones filtradas por búsqueda
		filteredOptions() {
			const q = (this.searchText || '').trim().toLowerCase();
			const list = this.baseOptions;
			if (!q) return list;
			return list.filter(o =>
				String(o[this.labelKey] || '')
					.toLowerCase()
					.includes(q)
			);
		},
	},
	watch: {
		value(v) {
			this.innerValue = v;
			const item = (this.options || []).find(o => o[this.valueKey] === v);
			this.searchText = item ? item[this.labelKey] : '';
		},
		options(newOpts) {
			if (this.pendingOpen && Array.isArray(newOpts) && newOpts.length) {
				if (window[ACTIVE_KEY] === this._uid) this.openDropdownSoon();
				this.pendingOpen = false;
			}
		},
		// Abrir el dropdown si cambió a loading=false y estaba pendiente
		loading(newVal, oldVal) {
			if (this.pendingOpen && newVal === true) {
				if (window[ACTIVE_KEY] === this._uid) this.openDropdownSoon();
			}
			if (oldVal === true && newVal === false && this.pendingOpen && (this.options || []).length) {
				if (window[ACTIVE_KEY] === this._uid) this.openDropdownSoon();
				this.pendingOpen = false;
			}
		},
		searchText(q) {
			this.$emit('query', (q || '').trim());
		},
	},
	methods: {
		// Abrir el dropdown (si no está abierto)
		onInteract() {
			if (this.disabled) return;

			const inp = this.$refs.input;
			if (inp && typeof inp.focus === 'function') inp.focus();

			window[ACTIVE_KEY] = this._uid;
			window.dispatchEvent(new CustomEvent('ios-select-open', { detail: this._uid }));

			if (!this.loading && (!this.options || this.options.length === 0)) {
				this.pendingOpen = true;
				this.$emit('open', true);
				this.$nextTick(() => this.openDropdownSoon());
				return;
			}

			this.openDropdownSoon();
		},
		// Abrir el dropdown (si no está abierto) rapido
		openDropdownSoon() {
			clearTimeout(this.openTimer);
			this.openTimer = setTimeout(() => {
				if (window[ACTIVE_KEY] !== this._uid) return;
				const sel = this.$refs.sel;
				if (sel && typeof sel.toggleMenu === 'function' && !sel.visible) {
					sel.toggleMenu();
				}
			}, 80);
		},
		// Evento de ElSelect: cambió visible
		onVisibleChange(open) {
			this.menuOpen = open;
			this.$emit('visible-change', open);
			if (open) {
				if (!this.scrollLock) this.scrollLock = createLocalScrollLock();

				this.addScrollGuards();
				this.refocusSoon();

				this.$nextTick(() => {
					const pop = this.$refs.sel && this.$refs.sel.$refs && this.$refs.sel.$refs.popper;
					const popEl = pop && (pop.$el || pop);
					const wrap =
						popEl &&
						(popEl.querySelector('.el-select-dropdown .el-scrollbar__wrap') ||
							popEl.querySelector('.el-scrollbar__wrap') ||
							popEl.querySelector('.el-select-dropdown__wrap'));
					// host = raíz del componente para detectar su contenedor scrolleable
					const host = this.$el;
					this.scrollLock.lock(host, wrap || null);

					if (wrap) {
						if (!wrap.hasAttribute('tabindex')) wrap.setAttribute('tabindex', '-1');
						wrap.focus();
					}
					const first = popEl && popEl.querySelector('.el-select-dropdown__item');
					if (first) {
						if (!first.hasAttribute('tabindex')) first.setAttribute('tabindex', '-1');
						first.focus();
					}
				});
			} else {
				this.pendingOpen = false;
				this.removeScrollGuards();
				if (this.scrollLock) this.scrollLock.unlock();
				clearTimeout(this.refocusTimer);
				if (this.clearOnOutsideNoValue && (this.innerValue === '' || this.innerValue == null)) {
					this.searchText = '';
				}
			}
		},
		onBlurInput() {
			if (this.menuOpen) this.refocusSoon();
		},
		refocusSoon() {
			clearTimeout(this.refocusTimer);
			this.refocusTimer = setTimeout(() => {
				const inp = this.$refs.input;
				if (!inp || typeof inp.focus !== 'function') return;
				inp.focus();
				try {
					const len = (this.searchText || '').length;
					if (typeof inp.setSelectionRange === 'function') {
						inp.setSelectionRange(len, len);
					}
				} catch (_) {}
			}, 0);
		},
		onChange(val) {
			this.$emit('input', val);
			this.$emit('change', val);

			const item = (this.options || []).find(o => o[this.valueKey] === val);

			if (this.clearOnSelect) {
				this.searchText = '';
			} else {
				if (item) this.searchText = item[this.labelKey];
			}
		},
		clearInput() {
			this.searchText = '';
			if (this.clearAlsoValue) {
				this.innerValue = null;
				this.$emit('input', null);
				this.$emit('change', null);
				this.$emit('clear', this.field || null);
			}
			this.refocusSoon();
			this.openDropdownSoon();
		},
		// Cerrar el dropdown (si está abierto)
		closeMenu() {
			const sel = this.$refs.sel;
			if (sel && sel.visible && typeof sel.toggleMenu === 'function') {
				sel.toggleMenu(); // cierra
			}
		},
		// Agregar listeners para cerrar si se scrollea fuera
		addScrollGuards() {
			const handler = () => {
				const inp = this.$refs.input;
				if (!inp) return;
				const r = inp.getBoundingClientRect();
				const vh = window.innerHeight || document.documentElement.clientHeight;
				const vw = window.innerWidth || document.documentElement.clientWidth;
				const out = r.bottom < 0 || r.top > vh || r.right < 0 || r.left > vw;
				if (out) this.closeMenu();
			};

			window.addEventListener('scroll', handler, { passive: true, capture: true });
			document.addEventListener('scroll', handler, { passive: true, capture: true });

			this.scrollListeners.push(['window', handler]);
			this.scrollListeners.push(['document', handler]);
		},
		// Quitar listeners de scroll
		removeScrollGuards() {
			for (const [target, fn] of this.scrollListeners) {
				const t = target === 'window' ? window : document;
				t.removeEventListener('scroll', fn, { capture: true });
			}
			this.scrollListeners = [];
		},
		// Actualizar el scroll lock (si está activo) pronto
		focusDropdownSoon() {
			this.$nextTick(() => {
				const sel = this.$refs.sel;
				if (!sel || !sel.$refs) return;

				const pop = sel.$refs.popper;
				const popEl = pop && (pop.$el || pop);
				if (!popEl || typeof popEl.querySelector !== 'function') return;

				const wrap =
					popEl.querySelector('.el-select-dropdown .el-scrollbar__wrap') ||
					popEl.querySelector('.el-scrollbar__wrap') ||
					popEl.querySelector('.el-select-dropdown__wrap');

				if (wrap) {
					if (!wrap.hasAttribute('tabindex')) wrap.setAttribute('tabindex', '-1');
					try {
						wrap.focus({ preventScroll: true });
					} catch (e) {
						wrap.focus();
					}
				}

				const first = popEl.querySelector('.el-select-dropdown__item');
				if (first) {
					if (!first.hasAttribute('tabindex')) first.setAttribute('tabindex', '-1');
					try {
						first.focus({ preventScroll: true });
					} catch (e) {
						first.focus();
					}
				}
			});
		},
	},
	mounted() {
		// Guardo el evento global para cerrar si otro se abre dropdown
		this._onGlobalOpen = e => {
			if (!e || e.detail === this._uid) return;
			const sel = this.$refs.sel;
			if (sel && sel.visible) {
				sel.toggleMenu();
				this.menuOpen = false;
				this.pendingOpen = false;
				clearTimeout(this.openTimer);
			}
		};
		window.addEventListener('ios-select-open', this._onGlobalOpen);
	},
	beforeDestroy() {
		clearTimeout(this.openTimer);
		this.removeScrollGuards();
		window.removeEventListener('ios-select-open', this._onGlobalOpen);
		if (this.scrollLock) this.scrollLock.unlock();
	},
};
</script>
<style scoped>
.ios-select-proxy {
	position: relative;
	width: 100%;
}
.ios-select-proxy .arrow-icon {
	position: absolute;
	right: 10px;
	top: 50%;
	transform: translateY(-50%);
	font-size: 14px;
	color: #909399;
	pointer-events: none;
	z-index: 2;
}

.ios-select-proxy.has-clear .arrow-icon {
	right: 36px;
}

.ios-select-proxy.is-open .arrow-icon {
	transform: translateY(-50%) rotate(180deg);
	transition: transform 120ms ease;
}

.proxy-input {
	position: relative;
	z-index: 2;
	width: 100%;
	height: 40px;
	box-sizing: border-box;
	padding: 0 32px 0 12px;
	border: 1px solid #d6d6d6;
	border-radius: 8px;
	font-size: 14px;
	background: #fff;
	outline: none;
	cursor: text;
}
.clear-btn {
	position: absolute;
	right: 8px;
	top: 50%;
	transform: translateY(-50%);
	height: 24px;
	width: 24px;
	border: none;
	border-radius: 12px;
	background: #f2f3f5;
	font-size: 14px;
	line-height: 24px;
	text-align: center;
	cursor: pointer;
	z-index: 3;
}
.clase-el-select {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 40px;
	opacity: 0;
	pointer-events: none;
	margin: 0;
	z-index: 1;
}
.no-data-hint {
	padding: 8px 12px;
	text-align: center;
	font-size: 13px;
	color: #c0c4cc;
}
@media (max-width: 768px) {
	.ios-option {
		white-space: normal;
		overflow: hidden;
		text-overflow: ellipsis;
		line-height: 14px;
		padding: 20px 10px;
	}
}
</style>
